// ═══════════════════════════════════════════════════════════════════
//  AVAILABILITY WEEK MODEL (AB#803)
//
//  The pure half of the availability page's dated week (behind
//  AmsAvailabilityWeekView): which Monday is showing, how the week is
//  named, how it travels in the URL next to site, view and host, and
//  how a week endpoint's cells (AB#802) become the grid the editor
//  already draws. Nothing here renders or fetches (weekModel.test.ts).
//
//  Every date is a Pacific calendar date ("2026-10-19"). "This week"
//  is read off the Pacific clock, never the browser's.
// ═══════════════════════════════════════════════════════════════════

import {
  addDays,
  type CalendarDate,
  calendarDateParts,
  MONTH_NAMES,
  mondayOf,
  SITE_TIME_ZONE,
  todayInZone,
  WEEKDAYS,
  weekdayIndexOf,
} from "@/app/booking-calendar/lib/siteTime"
import type { SlotPlanWeekDto } from "../availabilityApi"
import {
  type CellState,
  cellKey,
  decodeWeekday,
  type EditorKind,
  type EditorState,
  frameFor,
  GRID_WEEKDAYS,
  timeToMinutes,
} from "./editorModel"

export { mondayOf }

// ── Which week ──────────────────────────────────────────────────────

/** The Monday of the current Pacific week. `now` is the clock, injectable for tests. */
export function thisWeek(now: Date = new Date(), timeZone: string = SITE_TIME_ZONE): CalendarDate {
  return mondayOf(todayInZone(timeZone, now))
}

/** The Monday `delta` weeks from the week containing `date` (−1 the previous week, +1 the next). */
export function stepWeek(date: CalendarDate, delta: number): CalendarDate {
  return addDays(mondayOf(date), 7 * delta)
}

/** Whether `monday`'s week is the week containing `today`. */
export function isCurrentWeek(monday: CalendarDate, today: CalendarDate): boolean {
  return mondayOf(monday) === mondayOf(today)
}

/** Whether `monday`'s week ended before the week containing `today` began: viewable, read-only. */
export function isPastWeek(monday: CalendarDate, today: CalendarDate): boolean {
  return mondayOf(monday) < mondayOf(today)
}

// ── Naming it ───────────────────────────────────────────────────────

/** "2026-10-19" → "Mon 10/19": a dated column's header. */
export function dayHeader(date: CalendarDate): string {
  const { month, day } = calendarDateParts(date)!
  return `${WEEKDAYS[weekdayIndexOf(date)]!.slice(0, 3)} ${month}/${day}`
}

/** "2026-10-19" → "Monday 10/19": a dated cell's accessible name before its time. */
export function dayLabel(date: CalendarDate): string {
  const { month, day } = calendarDateParts(date)!
  return `${WEEKDAYS[weekdayIndexOf(date)]} ${month}/${day}`
}

/** The stepper's title, Monday to Friday: "Oct 19 – Oct 23, 2026", or "Dec 28, 2026 – Jan 1, 2027". */
export function weekRangeLabel(date: CalendarDate): string {
  const monday = mondayOf(date)
  const friday = addDays(monday, 4)
  const first = calendarDateParts(monday)!
  const last = calendarDateParts(friday)!
  const short = (p: { month: number; day: number }) => `${MONTH_NAMES[p.month - 1]} ${p.day}`
  return first.year === last.year
    ? `${short(first)} – ${short(last)}, ${last.year}`
    : `${short(first)}, ${first.year} – ${short(last)}, ${last.year}`
}

/** One dated column: its DayOfWeek (1 = Monday … 5 = Friday), date and names. */
export interface WeekColumn {
  weekday: number
  date: CalendarDate
  /** "Mon 10/19" */
  header: string
  /** "Monday 10/19" */
  label: string
}

/** Monday to Friday of the week containing `date`. */
export function weekColumns(date: CalendarDate): WeekColumn[] {
  const monday = mondayOf(date)
  return GRID_WEEKDAYS.map(weekday => {
    const day = addDays(monday, weekday - 1)
    return { weekday, date: day, header: dayHeader(day), label: dayLabel(day) }
  })
}

// ── The URL ─────────────────────────────────────────────────────────

/** The query parameter the shown week travels in: its Monday, "yyyy-MM-dd". */
export const WEEK_PARAM = "week"

/**
 * The week a `week` parameter asks for: its Monday, whatever day of the week it names; null when it
 * is absent or not a real date (the page then opens on this week).
 */
export function parseWeekParam(value: string | null | undefined): CalendarDate | null {
  if (!value || !calendarDateParts(value)) return null
  return mondayOf(value)
}

/** What the availability page keeps in its URL. */
export interface AvailabilitySelection {
  site: string
  view: "site" | "host"
  host: number | null
  /** The Monday showing, or null for "this week" (no parameter). */
  week: CalendarDate | null
}

/** Reads the page's URL. A missing or unreadable choice reads as none. */
export function readAvailabilityParams(params: URLSearchParams): AvailabilitySelection {
  const hostParam = Number(params.get("host"))
  return {
    site: params.get("site") ?? "",
    view: params.get("view") === "host" ? "host" : "site",
    host: Number.isInteger(hostParam) && hostParam > 0 ? hostParam : null,
    week: parseWeekParam(params.get(WEEK_PARAM)),
  }
}

/**
 * The page's URL for a selection: site, then view and host (a host only on the host view), then the
 * week. The inverse of readAvailabilityParams, so a link reopens the same schedule and week.
 */
export function availabilitySearch(selection: AvailabilitySelection): URLSearchParams {
  const search = new URLSearchParams()
  if (selection.site) search.set("site", selection.site)
  if (selection.view === "host") {
    search.set("view", "host")
    if (selection.host) search.set("host", String(selection.host))
  }
  const week = selection.week ? parseWeekParam(selection.week) : null
  if (week) search.set(WEEK_PARAM, week)
  return search
}

// ── The endpoint's cells ────────────────────────────────────────────

const EFFECTIVE_STATE_BY_NUMBER: readonly CellState[] = ["unavailable", "available", "public", "private", "managed"]

/**
 * An EffectiveCellState as sent (0 unavailable, 1 available, 2 public, 3 private, 4 managed), or its
 * name should a string converter ever be added; null when unreadable. These are the editor's own
 * cell states, so the grid draws a dated cell exactly as it draws a default one.
 */
export function decodeEffectiveState(value: number | string | null | undefined): CellState | null {
  if (typeof value === "number") return EFFECTIVE_STATE_BY_NUMBER[value] ?? null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeEffectiveState(Number(value))
  const lowered = value.toLowerCase() as CellState
  return EFFECTIVE_STATE_BY_NUMBER.includes(lowered) ? lowered : null
}

/** A week endpoint's answer, made drawable. */
export interface WeekView {
  /** The Monday the server answered for. */
  monday: CalendarDate
  columns: WeekColumn[]
  /**
   * The week's EFFECTIVE states as an editor state, so AvailabilityGrid draws it unchanged: frame from
   * the week's own slot length, window and breaks; open cells by cellKey(weekday, start). Never edited
   * or saved: the settings fields are blank.
   */
  state: EditorState
  /** The overrides that changed each cell, by cellKey; only cells an override changed are present. */
  overrideIdsByCell: Record<string, string[]>
  /** The week's overrides as the endpoint summarised them. */
  overrides: SlotPlanWeekDto["overrides"]
  /** Cells the endpoint sent that fall outside the frame or could not be read. */
  droppedCells: number
}

/** The drawable week, or null when its window or slot length cannot be read. */
export function weekViewFrom(kind: EditorKind, dto: SlotPlanWeekDto): WeekView | null {
  const frame = frameFor(dto)
  const monday = parseWeekParam(dto.weekStart)
  if (!frame || !monday) return null

  const cells: Record<string, CellState> = {}
  const overrideIdsByCell: Record<string, string[]> = {}
  let droppedCells = 0

  for (const day of dto.days ?? []) {
    const fromDate = parseWeekParam(day.date) === monday ? weekdayIndexOf(day.date) + 1 : null
    const weekday = fromDate ?? decodeWeekday(day.weekday)
    for (const cell of day.cells ?? []) {
      const start = timeToMinutes(cell.start)
      const state = decodeEffectiveState(cell.effectiveState)
      if (weekday == null || !GRID_WEEKDAYS.includes(weekday) || start == null || !frame.rows.includes(start) || !state) {
        droppedCells++
        continue
      }
      const key = cellKey(weekday, start)
      if (state !== "unavailable") cells[key] = state
      if (cell.overrideIds?.length) overrideIdsByCell[key] = [...cell.overrideIds]
    }
  }

  return {
    monday,
    columns: weekColumns(monday),
    state: { kind, frame, cells, activeFrom: "", activeUntil: null, maxPerDay: "", version: dto.planVersion ?? null },
    overrideIdsByCell,
    overrides: dto.overrides ?? [],
    droppedCells,
  }
}
